/**
 * BrandLogo — official FinminOS wordmark, drawn as inline SVG.
 *
 * Why SVG (and not the brand-kit PNGs):
 *   - The PNGs ship with solid Forest / Cream backgrounds, which clash on
 *     translucent surfaces, gradients, and any color other than the one
 *     they were exported on.
 *   - SVG scales without blur and inherits color, so the "Finmin" word can
 *     follow `currentColor` (no separate light/dark file needed).
 *
 * Composition mirrors the brand-kit logo exactly:
 *   "Finmin"   → serif word in the surface text color
 *   amber ◯    → radial-gradient sphere taking the place of the "O"
 *   "S"        → serif letter filled with the same amber gradient
 *
 * The font is the same display family used everywhere else in the app
 * (Cormorant Garamond → Optima → Georgia), loaded once globally by
 * `packages/ui/src/styles/globals.css`.
 */
import * as React from 'react';
import { cn } from '../lib/cn';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface BrandLogoProps extends Omit<React.SVGProps<SVGSVGElement>, 'viewBox'> {
  /** Color of the "Finmin" word. Default: surface text color via currentColor.
   *  Pass `'cream'`, `'forest'`, or any CSS color to override. */
  wordColor?: 'cream' | 'forest' | string;
  /** Pixel height. Width auto-scales from the wordmark's intrinsic ratio. */
  height?:    number;
  /** Accessible name. Default "FinminOS". */
  title?:     string;
  className?: string;
}

// Resolve the cream / forest aliases to their token-driven hsl(...) values.
function resolveWordColor(input: BrandLogoProps['wordColor']): string {
  if (input === 'cream')  return 'hsl(var(--palette-cream, 40 100% 98%))';
  if (input === 'forest') return 'hsl(var(--palette-forest, 176 72% 17%))';
  return input ?? 'currentColor';
}

// ── BrandLogo ─────────────────────────────────────────────────────────────────

/* Wordmark layout (computed empirically against Cormorant Garamond 500 @ 160px):
 *
 *   "Finmin"          x=6   …  ends ≈ x=486   (textLength locks this exactly)
 *   sphere (the "o")  cx=545, r=58   →  spans 487 … 603
 *   "S"               x=615 … ≈ 695
 *
 * Numbers are tuned with a small breathing gap on either side of the sphere
 * so it reads as "Finmin · O · S" rather than overlapping the last "n". */
const VIEW_W = 720;
const VIEW_H = 180;
const FINMIN_LEN = 480;   // textLength px — locks rendered width across browsers

let _gradId = 0;

export function BrandLogo({
  wordColor,
  height = 32,
  title  = 'FinminOS',
  className,
  ...rest
}: BrandLogoProps) {
  // Per-instance gradient id so multiple logos on a page don't collide.
  const uid = React.useMemo(() => `finminos-sphere-${++_gradId}`, []);
  const word = resolveWordColor(wordColor);

  return (
    <svg
      role="img"
      aria-label={title}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      height={height}
      // Width auto-scales from the viewBox ratio so callers only set height.
      style={{ width: 'auto', height, display: 'block' }}
      className={cn('select-none', className)}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <title>{title}</title>

      <defs>
        {/* Coin body — bright cream-yellow highlight at upper-left fading
            to a warm golden-amber rim, matching the brand-template sphere. */}
        <radialGradient id={uid} cx="32%" cy="26%" r="78%">
          <stop offset="0%"   stopColor="#FFF3CC" />
          <stop offset="22%"  stopColor="#FFD571" />
          <stop offset="55%"  stopColor="#F5A937" />
          <stop offset="85%"  stopColor="#D17A1B" />
          <stop offset="100%" stopColor="#9A5A12" />
        </radialGradient>
        {/* Inner shadow at the bottom-right for 3D depth. */}
        <radialGradient id={`${uid}-shade`} cx="72%" cy="78%" r="50%">
          <stop offset="0%"   stopColor="#5A3300" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#5A3300" stopOpacity="0"    />
        </radialGradient>
        {/* Glossy highlight blob — small soft hotspot upper-left. */}
        <radialGradient id={`${uid}-glow`} cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#FFFFFF" stopOpacity="0.85" />
          <stop offset="55%"  stopColor="#FFFFFF" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0"    />
        </radialGradient>
      </defs>

      {/* "Finmin" — width locked via textLength so layout stays stable
          across Chrome / Safari / Firefox and any fallback fonts. */}
      <text
        x="6"
        y="138"
        fontFamily="'Cormorant Garamond', 'Optima', 'Optima nova', Georgia, serif"
        fontSize="160"
        fontWeight="500"
        letterSpacing="-2"
        textLength={FINMIN_LEN}
        lengthAdjust="spacingAndGlyphs"
        fill={word}
      >
        Finmin
      </text>

      {/* Amber coin sphere — sits in the "O" slot, just right of "Finmin".
          Built up in five layers, mimicking the brand template:
            1. Body  — golden radial gradient.
            2. Shade — soft inner shadow at the bottom-right for 3D.
            3. Gloss — small white hotspot at the upper-left.
            4. Mill  — dashed ring around the perimeter (coin milling).
            5. Inner — thin solid ring just inside the milled edge. */}
      <g>
        {/* 1. Body */}
        <circle cx="555" cy="92" r="58" fill={`url(#${uid})`} />
        {/* 2. Shade */}
        <circle cx="555" cy="92" r="58" fill={`url(#${uid}-shade)`} />
        {/* 3. Gloss highlight — soft white blob upper-left */}
        <ellipse
          cx="535"
          cy="72"
          rx="18"
          ry="11"
          fill={`url(#${uid}-glow)`}
          transform="rotate(-28 535 72)"
        />
        {/* 4. Milled coin edge — chunky dashed ring, clearly visible
              even when the sphere is rendered at 38–44 px in the navbar.
              Dasharray "3 4.5" gives ~46 ticks around the perimeter. */}
        <circle
          cx="555"
          cy="92"
          r="55"
          fill="none"
          stroke="#6B3A05"
          strokeOpacity="0.80"
          strokeWidth="5"
          strokeDasharray="3 4.5"
        />
        {/* 5. Inner rim — solid ring inside the milled edge,
              gives the coin a clear "face" boundary. */}
        <circle
          cx="555"
          cy="92"
          r="49"
          fill="none"
          stroke="#6B3A05"
          strokeOpacity="0.45"
          strokeWidth="2"
        />
        {/* Outer hairline — crisp silhouette on cream surfaces */}
        <circle
          cx="555"
          cy="92"
          r="58"
          fill="none"
          stroke="#6B3A05"
          strokeOpacity="0.60"
          strokeWidth="1.6"
        />
      </g>

      {/* "S" — same amber gradient as the sphere. */}
      <text
        x="618"
        y="138"
        fontFamily="'Cormorant Garamond', 'Optima', 'Optima nova', Georgia, serif"
        fontSize="160"
        fontWeight="500"
        letterSpacing="-2"
        fill={`url(#${uid})`}
      >
        S
      </text>
    </svg>
  );
}

// ── Logomark (just the amber sphere) ──────────────────────────────────────────

export interface BrandLogomarkProps extends Omit<React.SVGProps<SVGSVGElement>, 'viewBox'> {
  size?:  number;
  title?: string;
  className?: string;
}

/**
 * BrandLogomark — square amber coin only, for tight spots
 * (mobile navbar, favicon-sized chrome, avatar fallback).
 *
 * Uses the same five-layer composition as the full <BrandLogo /> so the
 * coin reads identically at any scale: warm body, lower-right shade, glossy
 * highlight, milled coin edge, inner rim, outer hairline.
 */
export function BrandLogomark({
  size = 32,
  title = 'FinminOS',
  className,
  ...rest
}: BrandLogomarkProps) {
  const uid = React.useMemo(() => `finminos-mark-${++_gradId}`, []);
  return (
    <svg
      role="img"
      aria-label={title}
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={cn('block shrink-0 select-none', className)}
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <title>{title}</title>
      <defs>
        {/* Warm golden body — matches BrandLogo's gradient stops. */}
        <radialGradient id={uid} cx="32%" cy="26%" r="78%">
          <stop offset="0%"   stopColor="#FFF3CC" />
          <stop offset="22%"  stopColor="#FFD571" />
          <stop offset="55%"  stopColor="#F5A937" />
          <stop offset="85%"  stopColor="#D17A1B" />
          <stop offset="100%" stopColor="#9A5A12" />
        </radialGradient>
        <radialGradient id={`${uid}-shade`} cx="72%" cy="78%" r="50%">
          <stop offset="0%"   stopColor="#5A3300" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#5A3300" stopOpacity="0"    />
        </radialGradient>
        <radialGradient id={`${uid}-glow`} cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#FFFFFF" stopOpacity="0.85" />
          <stop offset="55%"  stopColor="#FFFFFF" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0"    />
        </radialGradient>
      </defs>

      {/* 1. Body */}
      <circle cx="50" cy="50" r="46" fill={`url(#${uid})`} />
      {/* 2. Shade */}
      <circle cx="50" cy="50" r="46" fill={`url(#${uid}-shade)`} />
      {/* 3. Gloss */}
      <ellipse cx="38" cy="32" rx="11" ry="7" fill={`url(#${uid}-glow)`} transform="rotate(-28 38 32)" />
      {/* 4. Milled coin edge */}
      <circle cx="50" cy="50" r="43" fill="none" stroke="#6B3A05" strokeOpacity="0.80" strokeWidth="4" strokeDasharray="2.4 3.6" />
      {/* 5. Inner rim */}
      <circle cx="50" cy="50" r="38" fill="none" stroke="#6B3A05" strokeOpacity="0.45" strokeWidth="1.6" />
      {/* 6. Outer hairline */}
      <circle cx="50" cy="50" r="46" fill="none" stroke="#6B3A05" strokeOpacity="0.60" strokeWidth="1.3" />
    </svg>
  );
}
